/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Anchor, Box } from '@atlaskit/primitives/compiled';
import { HoverCard } from '@atlaskit/smart-card/hover-card';

import { onJiraIssueKeyClicked } from '../../../../../ai-answer-dialog/on-jira-issue-key-clicked';
import type { NonGenerativeAIAnswerResultShownProps } from '../../../../../ai-answer-dialog/types';
import { SainAction } from '../../../../../common/controller/analytics/hooks/sain-action';
import { useAnalytics } from '../../../../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../../../../common/utils/sain-feature-control';
import { onNonGenerativeKnowledgeCardLinkClick } from '../../../../../knowledge-cards/on-non-generative-knowledge-card-link-click';

const styles = cssMap({
	noWrap: {
		whiteSpace: 'nowrap',
	},
});

export const IssueKeyNode: ({
	issuekey,
	url,
	analyticsAttributes,
	fireEngagementEvent,
}: {
	issuekey: string;
	url: string;
	analyticsAttributes: NonGenerativeAIAnswerResultShownProps;
	fireEngagementEvent: (sainAction: SainAction) => void;
}) => JSX.Element = ({
	issuekey,
	url,
	analyticsAttributes,
	fireEngagementEvent,
}: {
	issuekey: string;
	url: string;
	analyticsAttributes: NonGenerativeAIAnswerResultShownProps;
	fireEngagementEvent: (sainAction: SainAction) => void;
}) => {
	const [fireAnalyticsEvent] = useAnalytics();

	const handleClick = useCallback(
		(e?: React.MouseEvent) => {
			const isAuxClick = e?.button === 1;
			const isContextMenu = e?.button === 2;
			if ((isAuxClick || isContextMenu) && !SAINFeatureControl.isNonPrimaryClickTrackingEnabled()) {
				return;
			}

			fireAnalyticsEvent(
				onJiraIssueKeyClicked({ ...analyticsAttributes, issuekey, isAuxClick, isContextMenu }),
			);
			fireAnalyticsEvent(
				onNonGenerativeKnowledgeCardLinkClick({
					...analyticsAttributes,
					isAuxClick,
					isContextMenu,
				}),
			);
			if (fg('kd_fix_nl2jql_mau_instrumentation')) {
				fireEngagementEvent(SainAction.JIRA_NL_TO_JQL_WORK_ITEM_CLICKED);
			}
		},
		[fireAnalyticsEvent, fireEngagementEvent, analyticsAttributes, issuekey],
	);

	return (
		<Box
			onAuxClick={(e: React.MouseEvent) => {
				if (e.button !== 1) {
					return;
				}
				handleClick(e);
			}}
			onContextMenu={handleClick}
		>
			<HoverCard url={url}>
				<Anchor href={url} xcss={styles.noWrap} onClick={handleClick}>
					{issuekey}
				</Anchor>
			</HoverCard>
		</Box>
	);
};
